import { createServerFn } from "@tanstack/react-start";
import {
  getSubscriptionApiWorkspacesWorkspaceIdBillingSubscriptionGet,
  getCreditsApiWorkspacesWorkspaceIdBillingCreditsGet,
  getBillingInfoApiWorkspacesWorkspaceIdBillingBillingInfoGet,
  updateBillingInfoApiWorkspacesWorkspaceIdBillingBillingInfoPut,
  submitUpgradeApiWorkspacesWorkspaceIdBillingSubscriptionUpgradePost,
  submitDowngradeApiWorkspacesWorkspaceIdBillingSubscriptionDowngradePost,
  submitCancelApiWorkspacesWorkspaceIdBillingSubscriptionCancelPost,
  submitUndoCancelApiWorkspacesWorkspaceIdBillingSubscriptionUndoCancelPost,
  submitAddonApiWorkspacesWorkspaceIdBillingCreditsAddonPost,
  consumeCreditsApiWorkspacesWorkspaceIdBillingCreditsConsumePost,
  getCreditsUsageApiWorkspacesWorkspaceIdBillingCreditsUsageGet,
  listCreditTransactionsApiWorkspacesWorkspaceIdBillingCreditsTransactionsGet,
  createSubscriptionCheckoutApiWorkspacesWorkspaceIdBillingSubscriptionCheckoutPost,
  createCreditsCheckoutApiWorkspacesWorkspaceIdBillingCreditsCheckoutPost,
  createCustomerPortalApiWorkspacesWorkspaceIdBillingCustomerPortalPost,
} from "@/lib/api/generated/billing/billing";
import {
  listPlansApiBillingPlansGet,
  getStripeConfigApiBillingStripeConfigGet,
} from "@/lib/api/generated/billing-plans/billing-plans";
import type {
  UpdateBillingInfoRequest,
  UpgradeSubscriptionRequest,
  DowngradeSubscriptionRequest,
  SubscriptionChangeRequest,
  AddonCreditsRequest,
  ConsumeCreditsRequest,
  PlanResponse,
  SubscriptionCheckoutRequest,
  CreditsCheckoutRequest,
  CustomerPortalRequest,
  ListCreditTransactionsApiWorkspacesWorkspaceIdBillingCreditsTransactionsGetParams,
} from "@/lib/api/generated";
import { extractBillingErrorDetail, normalizeBillingErrorEnvelope } from "@/lib/api/billing-error";

type SerializableLimit = string | number | boolean | object | Array<unknown>;

type SerializablePlan = Omit<PlanResponse, "limits"> & {
  limits?: Record<string, SerializableLimit>;
};

function buildBillingError(params: {
  status: number;
  data: unknown;
  fallbackMessage: string;
}): Error {
  const message = normalizeBillingErrorEnvelope(params.data);
  const detail = extractBillingErrorDetail(params.data);
  return new Error(
    JSON.stringify({
      message: message === "An unexpected error occurred." ? params.fallbackMessage : message,
      ...(detail ? { error: detail } : {}),
      status: params.status,
    }),
  );
}

function throwOrWrapBillingError(params: { error: unknown; fallbackMessage: string }): never {
  if (params.error instanceof Error) {
    throw params.error;
  }

  const errorAsRecord =
    params.error && typeof params.error === "object"
      ? (params.error as Record<string, unknown>)
      : null;
  const status =
    errorAsRecord && typeof errorAsRecord.status === "number" ? errorAsRecord.status : 500;
  const data =
    errorAsRecord && "data" in errorAsRecord
      ? errorAsRecord.data
      : {
          error: {
            code: "UNKNOWN_ERROR",
            message: params.fallbackMessage,
          },
        };

  throw buildBillingError({ status, data, fallbackMessage: params.fallbackMessage });
}

export function normalizePlansResponse(data: unknown): SerializablePlan[] {
  if (Array.isArray(data)) {
    return data as SerializablePlan[];
  }

  if (data && typeof data === "object") {
    const record = data as Record<string, unknown>;
    const items = record.items;
    if (Array.isArray(items)) {
      return items as SerializablePlan[];
    }
  }

  return [];
}

// Read Functions

export const getPlans = createServerFn({ method: "GET" }).handler(async () => {
  try {
    const response = await listPlansApiBillingPlansGet();
    if (response.status !== 200) {
      throw buildBillingError({
        status: response.status,
        data: response.data,
        fallbackMessage: "Failed to get plans",
      });
    }

    const plans = normalizePlansResponse(response.data);
    if (plans.length === 0) {
      throw buildBillingError({
        status: 500,
        data: response.data,
        fallbackMessage: "Failed to get plans",
      });
    }

    return plans;
  } catch (error) {
    throwOrWrapBillingError({ error, fallbackMessage: "Failed to get plans" });
  }
});

export const getSubscription = createServerFn({ method: "GET" })
  .validator((workspaceId: string) => workspaceId)
  .handler(async ({ data: workspaceId }) => {
    try {
      const response =
        await getSubscriptionApiWorkspacesWorkspaceIdBillingSubscriptionGet(workspaceId);
      if (response.status !== 200) {
        const status = response.status as number;
        if (status === 404) {
          return null;
        }
        throw buildBillingError({
          status,
          data: response.data,
          fallbackMessage: "Failed to get subscription",
        });
      }
      return response.data;
    } catch (error) {
      throwOrWrapBillingError({ error, fallbackMessage: "Failed to get subscription" });
    }
  });

export const getCredits = createServerFn({ method: "GET" })
  .validator((workspaceId: string) => workspaceId)
  .handler(async ({ data: workspaceId }) => {
    try {
      const response = await getCreditsApiWorkspacesWorkspaceIdBillingCreditsGet(workspaceId);
      if (response.status !== 200) {
        const status = response.status as number;
        if (status === 404) {
          return null;
        }
        throw buildBillingError({
          status,
          data: response.data,
          fallbackMessage: "Failed to get credits",
        });
      }
      return response.data;
    } catch (error) {
      throwOrWrapBillingError({ error, fallbackMessage: "Failed to get credits" });
    }
  });

export const getBillingInfo = createServerFn({ method: "GET" })
  .validator((workspaceId: string) => workspaceId)
  .handler(async ({ data: workspaceId }) => {
    try {
      const response =
        await getBillingInfoApiWorkspacesWorkspaceIdBillingBillingInfoGet(workspaceId);
      if (response.status !== 200) {
        throw buildBillingError({
          status: response.status,
          data: response.data,
          fallbackMessage: "Failed to get billing info",
        });
      }
      return response.data;
    } catch (error) {
      throwOrWrapBillingError({ error, fallbackMessage: "Failed to get billing info" });
    }
  });

// Write Functions

export const updateBillingInfo = createServerFn({ method: "POST" })
  .validator((params: { workspaceId: string; data: UpdateBillingInfoRequest }) => params)
  .handler(async ({ data: { workspaceId, data } }) => {
    try {
      const response = await updateBillingInfoApiWorkspacesWorkspaceIdBillingBillingInfoPut(
        workspaceId,
        data,
      );
      if (response.status !== 200) {
        throw buildBillingError({
          status: response.status,
          data: response.data,
          fallbackMessage: "Failed to update billing info",
        });
      }
      return response.data;
    } catch (error) {
      throwOrWrapBillingError({ error, fallbackMessage: "Failed to update billing info" });
    }
  });

export const submitUpgrade = createServerFn({ method: "POST" })
  .validator((params: { workspaceId: string; data: UpgradeSubscriptionRequest }) => params)
  .handler(async ({ data: { workspaceId, data } }) => {
    try {
      const response = await submitUpgradeApiWorkspacesWorkspaceIdBillingSubscriptionUpgradePost(
        workspaceId,
        data,
      );
      if (response.status !== 202) {
        throw buildBillingError({
          status: response.status,
          data: response.data,
          fallbackMessage: "Failed to submit upgrade request",
        });
      }
      return response.data;
    } catch (error) {
      throwOrWrapBillingError({ error, fallbackMessage: "Failed to submit upgrade request" });
    }
  });

export const submitDowngrade = createServerFn({ method: "POST" })
  .validator((params: { workspaceId: string; data: DowngradeSubscriptionRequest }) => params)
  .handler(async ({ data: { workspaceId, data } }) => {
    try {
      const response =
        await submitDowngradeApiWorkspacesWorkspaceIdBillingSubscriptionDowngradePost(
          workspaceId,
          data,
        );
      if (response.status !== 202) {
        throw buildBillingError({
          status: response.status,
          data: response.data,
          fallbackMessage: "Failed to submit downgrade request",
        });
      }
      return response.data;
    } catch (error) {
      throwOrWrapBillingError({ error, fallbackMessage: "Failed to submit downgrade request" });
    }
  });

export const submitCancel = createServerFn({ method: "POST" })
  .validator((params: { workspaceId: string; data: SubscriptionChangeRequest }) => params)
  .handler(async ({ data: { workspaceId, data } }) => {
    try {
      const response = await submitCancelApiWorkspacesWorkspaceIdBillingSubscriptionCancelPost(
        workspaceId,
        data,
      );
      if (response.status !== 202) {
        throw buildBillingError({
          status: response.status,
          data: response.data,
          fallbackMessage: "Failed to submit cancel request",
        });
      }
      return response.data;
    } catch (error) {
      throwOrWrapBillingError({ error, fallbackMessage: "Failed to submit cancel request" });
    }
  });

export const submitUndoCancel = createServerFn({ method: "POST" })
  .validator((params: { workspaceId: string; data: SubscriptionChangeRequest }) => params)
  .handler(async ({ data: { workspaceId, data } }) => {
    try {
      const response =
        await submitUndoCancelApiWorkspacesWorkspaceIdBillingSubscriptionUndoCancelPost(
          workspaceId,
          data,
        );
      if (response.status !== 202) {
        throw buildBillingError({
          status: response.status,
          data: response.data,
          fallbackMessage: "Failed to submit undo-cancel request",
        });
      }
      return response.data;
    } catch (error) {
      throwOrWrapBillingError({ error, fallbackMessage: "Failed to submit undo-cancel request" });
    }
  });

export const submitAddon = createServerFn({ method: "POST" })
  .validator((params: { workspaceId: string; data: AddonCreditsRequest }) => params)
  .handler(async ({ data: { workspaceId, data } }) => {
    try {
      const response = await submitAddonApiWorkspacesWorkspaceIdBillingCreditsAddonPost(
        workspaceId,
        data,
      );
      if (response.status !== 202) {
        throw buildBillingError({
          status: response.status,
          data: response.data,
          fallbackMessage: "Failed to submit add-on request",
        });
      }
      return response.data;
    } catch (error) {
      throwOrWrapBillingError({ error, fallbackMessage: "Failed to submit add-on request" });
    }
  });

export const consumeCredits = createServerFn({ method: "POST" })
  .validator((params: { workspaceId: string; data: ConsumeCreditsRequest }) => params)
  .handler(async ({ data: { workspaceId, data } }) => {
    try {
      const response = await consumeCreditsApiWorkspacesWorkspaceIdBillingCreditsConsumePost(
        workspaceId,
        data,
      );
      if (response.status !== 200) {
        throw buildBillingError({
          status: response.status,
          data: response.data,
          fallbackMessage: "Failed to consume credits",
        });
      }
      return response.data;
    } catch (error) {
      throwOrWrapBillingError({ error, fallbackMessage: "Failed to consume credits" });
    }
  });

export const getCreditsUsage = createServerFn({ method: "GET" })
  .validator((workspaceId: string) => workspaceId)
  .handler(async ({ data: workspaceId }) => {
    try {
      const response =
        await getCreditsUsageApiWorkspacesWorkspaceIdBillingCreditsUsageGet(workspaceId);
      if (response.status !== 200) {
        throw buildBillingError({
          status: response.status,
          data: response.data,
          fallbackMessage: "Failed to get credits usage",
        });
      }
      return response.data;
    } catch (error) {
      throwOrWrapBillingError({ error, fallbackMessage: "Failed to get credits usage" });
    }
  });

export const listCreditTransactions = createServerFn({ method: "GET" })
  .validator(
    (params: {
      workspaceId: string;
      params?: ListCreditTransactionsApiWorkspacesWorkspaceIdBillingCreditsTransactionsGetParams;
    }) => params,
  )
  .handler(async ({ data: { workspaceId, params } }) => {
    try {
      const response =
        await listCreditTransactionsApiWorkspacesWorkspaceIdBillingCreditsTransactionsGet(
          workspaceId,
          params,
        );
      if (response.status !== 200) {
        throw buildBillingError({
          status: response.status,
          data: response.data,
          fallbackMessage: "Failed to list credit transactions",
        });
      }
      return response.data;
    } catch (error) {
      throwOrWrapBillingError({
        error,
        fallbackMessage: "Failed to list credit transactions",
      });
    }
  });

// === Stripe Checkout & Customer Portal ===

export const createSubscriptionCheckout = createServerFn({ method: "POST" })
  .validator((params: { workspaceId: string; data: SubscriptionCheckoutRequest }) => params)
  .handler(async ({ data: { workspaceId, data } }) => {
    try {
      const response =
        await createSubscriptionCheckoutApiWorkspacesWorkspaceIdBillingSubscriptionCheckoutPost(
          workspaceId,
          data,
        );
      if (response.status !== 200) {
        throw buildBillingError({
          status: response.status,
          data: response.data,
          fallbackMessage: "Failed to create checkout session",
        });
      }
      return response.data;
    } catch (error) {
      throwOrWrapBillingError({ error, fallbackMessage: "Failed to create checkout session" });
    }
  });

export const createCreditsCheckout = createServerFn({ method: "POST" })
  .validator((params: { workspaceId: string; data: CreditsCheckoutRequest }) => params)
  .handler(async ({ data: { workspaceId, data } }) => {
    try {
      const response =
        await createCreditsCheckoutApiWorkspacesWorkspaceIdBillingCreditsCheckoutPost(
          workspaceId,
          data,
        );
      if (response.status !== 200) {
        throw buildBillingError({
          status: response.status,
          data: response.data,
          fallbackMessage: "Failed to create checkout session",
        });
      }
      return response.data;
    } catch (error) {
      throwOrWrapBillingError({ error, fallbackMessage: "Failed to create checkout session" });
    }
  });

export const createCustomerPortal = createServerFn({ method: "POST" })
  .validator((params: { workspaceId: string; data: CustomerPortalRequest }) => params)
  .handler(async ({ data: { workspaceId, data } }) => {
    try {
      const response = await createCustomerPortalApiWorkspacesWorkspaceIdBillingCustomerPortalPost(
        workspaceId,
        data,
      );
      if (response.status !== 200) {
        throw buildBillingError({
          status: response.status,
          data: response.data,
          fallbackMessage: "Failed to create customer portal session",
        });
      }
      return response.data;
    } catch (error) {
      throwOrWrapBillingError({
        error,
        fallbackMessage: "Failed to create customer portal session",
      });
    }
  });

// === Stripe Config (for frontend initialization) ===

export const getStripeConfigServer = createServerFn({ method: "GET" }).handler(async () => {
  try {
    const response = await getStripeConfigApiBillingStripeConfigGet();
    if (response.status !== 200) {
      throw buildBillingError({
        status: response.status,
        data: response.data,
        fallbackMessage: "Failed to get Stripe config",
      });
    }
    return {
      publishableKey: response.data.publishable_key,
    };
  } catch (error) {
    throwOrWrapBillingError({ error, fallbackMessage: "Failed to get Stripe config" });
  }
});
